<template src="./chatter.html"></template>

<script lang="ts">
import { nextTick } from 'vue'
import { callTurnToConfigFn, encodeArray } from 'util/turnto-utils'
import chatterCommentsService from 'services/api/chatter-comments'
import chatterConfig from './chatter.config'
import globalStore from 'init/global-store'
import CalloutComment from 'components/comment/CalloutComment.vue'
import StaticMasonryLayout from 'layouts/masonry/StaticMasonryLayout.vue'
import Pagination from 'objects/pagination/Pagination.vue'
import trackingService from 'services/tracking-service'

export default {
  data() {
    // "this.cachedPages" is a variable used in this component, that is not reactive
    return {
      comments: [],
      currentPage: 1,
      globalStoreState: globalStore.state,
      offset: 0,
      previousSku: '',
      total: 0
    }
  },
  components: {
    'tt-comment': CalloutComment,
    'tt-pagination': Pagination,
    'tt-static-masonry-layout': StaticMasonryLayout
  },
  computed: {
    chatterConfig() {
      return chatterConfig
    },
    currentSku(): string {
      return this.globalStoreState.sku
    },
    limit() {
      return chatterConfig.limit
    }
  },
  methods: {
    fetchData(pageNum = 1) {
      if (this.currentSku && (this.offset === 0 || this.offset <= this.total)) {
        const isFirstCall = this.currentSku !== this.previousSku
        // TT-14051 - first 3 pages are cached
        if (!isFirstCall && this.offset <= (this.limit * 2)) {
          this.comments = this.cachedPages[Math.floor(this.offset / this.limit)]
          this.currentPage = pageNum
          // this.onCommentsChanged() // fires event to layout comments
          return
        }

        if (isFirstCall) {
          trackingService.queueEvent('chatter', 'load', this.currentSku)
        }

        const chatterTurnToConfig = window.turnToConfig.chatter || {}
        const displayLocales = chatterTurnToConfig.displayLocales || []
        chatterCommentsService.chatterComments({
          sku: this.currentSku,
          offset: this.offset,
          // TT-14051 - load first three pages on initial load
          limit: isFirstCall ? (this.limit * 3) : this.limit,
          sort: chatterConfig.sortOrder as 0 | 1,
          minCharCount: chatterConfig.minCharacterCount,
          minWordCount: chatterConfig.minWordCount,
          includeSyndicated: chatterConfig.includeSyndicated,
          includeRelated: true,
          displayLocales: encodeArray(displayLocales)
        }).then((data) => {
          this.previousSku = this.currentSku
          if (this.offset === 0 && data.comments.length < chatterConfig.minCount) {
            // not going to populate.  Does not meet the minimum comment count
            this.onFinish()
            return
          }
          this.comments = isFirstCall ? data.comments.slice(0, this.limit) : data.comments
          this.currentPage = pageNum
          this.total = data.total
          // this.onCommentsChanged() // fires event to layout comments
          if (isFirstCall) {
            this.onFinish() // just need to call when initial comments are loaded
            // TT-14051 - caching for first 3 pages
            this.cachedPages = []
            for (let i = 0; i < 3; i++) {
              this.cachedPages[i] = data.comments.splice(0, this.limit)
            }
          }
        }).catch(() => {
          // nothing necessary as widget won't show when there are no comments
        })
      }
    },
    onFinish() {
      nextTick(() => { callTurnToConfigFn('chatter', 'onFinish') })
    },
    pageChange(pageNum) {
      if (pageNum === this.currentPage) {
        return
      }
      this.offset = chatterConfig.limit * (pageNum - 1)
      this.fetchData(pageNum)
    }
  },
  watch: {
    // whenever the currentSku changes, we need to reload the comments
    currentSku() {
      this.offset = 0
      this.fetchData(1)
    }
  },
  created() {
    this.fetchData(1)
  }
}
</script>

<style lang="scss" src="./chatter.scss"></style>
